// The room list over HTTPS (MAJLIS-126; P-35, PROPOSED; D-55, EM-18, NF-4). Two reads, neither carrying a code:
//   GET  /api/rooms/listed   no credential: the rooms hosts chose to list (a handle, never the room id)
//   POST /api/rooms/mine     `Authorization: Bearer <identity id>.<identity secret>` (P-23's identity credential alone),
//                            the room ids IN THE BODY: which of this phone's own rooms are still active
// The server answers /mine only about the identity's own rooms; any other id is left out as if it did not exist.
import type { Fetched } from "./records";
import type { IdentityProof } from "./types";

export const ROOMS_LISTED_PATH = "/api/rooms/listed";
export const ROOMS_MINE_PATH = "/api/rooms/mine";

/** P-35: one room of the public list. */
export interface ListedRoom {
  handle: string;
  name: string;
  host_name: string;
  seated: number;
  max_seats: number;
  kind: "open" | "key";
}

/** P-35: one of this identity's own rooms. */
export interface MineRoom {
  room_id: string;
  active: boolean;
  role: "host" | "joined";
  name: string | null;
  host_name: string;
  seated: number;
  /** MAJLIS-145 (P-35, additive): ISO 8601 (UTC), the room was created - Your rooms' start time; absent from an older
   * server (the phone then falls back to its own first visit) */
  created?: string | null;
}

async function call<T>(fetchImpl: typeof fetch, url: string, init: RequestInit): Promise<Fetched<T>> {
  let res: Response;
  try {
    res = await fetchImpl(url, { ...init, cache: "no-store" });
  } catch {
    return { ok: false, why: "failed" };
  }
  if (res.status !== 200) return { ok: false, why: res.status === 404 ? "refused" : "failed" };
  try {
    return { ok: true, value: (await res.json()) as T };
  } catch {
    return { ok: false, why: "failed" };
  }
}

/** P-35: the listed rooms, most recently listed first. */
export function listedRooms(fetchImpl: typeof fetch): Promise<Fetched<{ rooms: ListedRoom[] }>> {
  return call(fetchImpl, ROOMS_LISTED_PATH, {});
}

/** P-35: which of these room ids are this identity's own, and whether each is still active. */
export function mineRooms(
  fetchImpl: typeof fetch,
  identity: IdentityProof,
  roomIds: readonly string[],
): Promise<Fetched<{ rooms: MineRoom[] }>> {
  return call(fetchImpl, ROOMS_MINE_PATH, {
    method: "POST",
    headers: { Authorization: `Bearer ${identity.id}.${identity.secret}`, "Content-Type": "application/json" },
    body: JSON.stringify({ room_ids: roomIds }),
  });
}
